/**
 * 表示言語の端末内保存 (localStorage、利用者ごと)。
 *
 * - キーは利用者 (userId) ごとに分ける。事務所の PC を複数の職員が使っても、
 *   ある職員の英語表示が別の職員へ持ち越されない。
 * - localStorage はプライベートブラウズ・保存の無効化で読めない / 書けないことがあるため、
 *   読み書きは必ず try/catch で包み、失敗は「保存なし」(= 既定の日本語) として扱う。
 */
import { isLocale, type Locale } from './types';

/** 保存キーの接頭辞。実際のキーは `{prefix}:{userId}`。 */
export const LOCALE_STORAGE_KEY_PREFIX = 'acecollect_admin_locale';

/** 利用者ごとの保存キー。userId が分からないときは接頭辞だけ (共有キー) を使う。 */
export function localeStorageKey(userId: string | null | undefined): string {
  const scope = typeof userId === 'string' ? userId.trim() : '';
  return scope ? `${LOCALE_STORAGE_KEY_PREFIX}:${scope}` : LOCALE_STORAGE_KEY_PREFIX;
}

/** 保存済みの表示言語。無い / 読めない / 未知の値は null。 */
export function readStoredLocale(userId: string | null | undefined): Locale | null {
  try {
    const raw = window.localStorage.getItem(localeStorageKey(userId));
    return isLocale(raw) ? raw : null;
  } catch {
    // 読めない環境 (保存の無効化など) は「保存なし」= 既定の日本語で表示する
    return null;
  }
}

/** 表示言語を保存する。書けなかったときは false (表示の切替自体はそのまま続ける)。 */
export function writeStoredLocale(userId: string | null | undefined, locale: Locale): boolean {
  try {
    window.localStorage.setItem(localeStorageKey(userId), locale);
    return true;
  } catch {
    // 書けない環境では、このページを開いている間だけ切り替わる (再読み込みで日本語に戻る)
    return false;
  }
}
